import {
	Attachment,
	AuditedRVRequest,
	defProfile,
	RequestorProfile,
	RVEnvironment,
	RVMode,
	RVPropertyType,
	RVRequest,
	RVRequestStatus,
	RVSearchType,
	SearchOption,
} from "./api/entities";
import {
	RV_REMOVE_PROPERTY,
	RV_RESET,
	RV_UPDATE_DOCS,
	RV_UPDATE_PROPERTY,
	RV_UPDATE_REASON,
	UPDATE_RV_ENV,
	UPDATE_RV_PROPERTIES,
	UPDATE_RV_SEARCH_OPTIONS,
} from "./core/actions";
import { defaultEnv } from "./RVContext";
import {
	getEjariActiveUserId,
	getEjariManagementId,
	getEjariUserType,
	getEmail,
	getEmiratesID,
	getMobileNumber,
	getName,
	getOwnerSSOAccountId,
	isLoggedIn
} from "@10x/sso";
import { TabuProfile } from "@10x/sso/TabuProfile";
import { EjariProfile } from "@10x/sso/EjariProfile";
import { Property, RVDocument, RVDocumentPage, RVProperty } from "./api/Property";
import Strings, { LVStrings } from "@my-dld/assets/strings";
import * as api from "./api";
import { getMCInfo, RVLookupX, uploadDocument, validateResponse, validateResponseX } from "./api/api";
import _ from "lodash";
import { StringUtil } from "@eres/core";
import { getAppServices } from "@10x/coreapp/index";

export function getSupportedRVMode( env: RVEnvironment ): RVMode[] {
	const r: RVMode[] = [];
	if ( env.mode ) {
		return [ env.mode ];
	}
	if ( env.isManagementCompany ) {
		r.push( RVMode.MANAGED );
	} else {
		r.push( RVMode.OWN );
		r.push( RVMode.TENANT );
	}
	if ( !env.isManagementCompany )
		r.push( RVMode.POA );
	return r;
}

export function getSearchModes( env: RVEnvironment ): RVSearchType[] {
	const r: RVSearchType[] = [];
	if ( env.mode != undefined && env.propertyType != undefined ) {
		if ( env.mode === RVMode.TENANT ) {
			r.push( RVSearchType.EjariNumber );
		} else if ( env.mode === RVMode.POA ) {
			r.push( RVSearchType.LandNumber );
			r.push( RVSearchType.DMNumber );
			r.push( RVSearchType.TitleDeed );
		} else {
			if ( TabuProfile.getParticipantId() ) r.push( RVSearchType.Participant );
			r.push( RVSearchType.LandNumber );
			r.push( RVSearchType.DMNumber );
			r.push( RVSearchType.EjariNumber );
			r.push( RVSearchType.TitleDeed );
			r.push( RVSearchType.Dewa );
		}
	}
	return r;
}

export function resetEnv() {
	getAppServices().storeService.getStore().dispatch(
		{
			type: UPDATE_RV_ENV,
			payload: _.cloneDeep( defaultEnv ),
		} );
}

export function resetIfEmpty() {
	const ps = getAppServices().storeService.getStore().getState().RValuation.properties;
	if ( ps == undefined || ps.length === 0 ) {
		resetEnv();
	}
}

export function setEnvUser() {
	const store = getAppServices().storeService.getStore();
	let env = store.getState().RValuation.env;
	if ( env == undefined ) {
		resetEnv();
		env = store.getState().RValuation.env;
	}
	if ( isLoggedIn() ) {
		const tp = TabuProfile.getProfile();
		if ( tp ) {
			env.participantId = Number( tp.id );
			let ssoId = getOwnerSSOAccountId();
			if ( ssoId && ssoId !== 0 ) {
				env.isEjariUser = true;
				env.userTypeId = 4;
			} else {
				env.isEjariUser = false;
				env.userTypeId = undefined;
			}
		} else {
			env.participantId = undefined;
		}
		const ep = EjariProfile.getProfile();
		if ( ep ) {
			env.isManagementCompany = true;
			env.managementId = getEjariManagementId();
			env.isEjariUser = true;
			env.userTypeId = getEjariUserType();
		} else {
			env.isManagementCompany = false;
			env.managementId = undefined;
		}
		env.eidNumber = getEmiratesID();
		env.phoneNumber = getMobileNumber();
		env.editableEid = StringUtil.isEmptyStringOrNull( env.eidNumber );
		env.editablePhone = StringUtil.isEmptyStringOrNull( env.phoneNumber );
	}
}

export function populateSearchOptions( opt: SearchOption, env: RVEnvironment ) {
	if ( env.isEjariUser ) {
		opt.isEjariUser = 1;
	}
	if ( env.participantId )
		opt.participantId = env.participantId;
	if ( env.managementId )
		opt.managementCompanyId = env.managementId;
	opt.userTypeId = env.userTypeId!;
	opt.requestorTypeId = env.mode!;
	opt.propertyTypeId = env.propertyType!;
	if ( opt.requestorTypeId === RVMode.TENANT ) {
		opt.emiratesId = env.eidNumber;
	}
}

export function getDocIdForUser( env: RVEnvironment ): number {
	switch ( env.mode ) {
		case RVMode.OWN:
			return 50;
		case RVMode.TENANT:
			return 49;
		case RVMode.POA:
			return 48;
		case RVMode.MANAGED:
			return 51;
		default:
			return 0;
	}
}

export function updateRVEnv( env: RVEnvironment ) {
	getAppServices().storeService.getStore().dispatch(
		{
			type: UPDATE_RV_ENV,
			payload: env,
		} );
}

export function setPropertiesOptions( properties: Property[], options: SearchOption, env: RVEnvironment ) {
	let store = getAppServices().storeService.getStore();
	store.dispatch(
		{
			type: UPDATE_RV_ENV,
			payload: env,
		} );
	store.dispatch(
		{
			type: UPDATE_RV_SEARCH_OPTIONS,
			payload: options,
		} );
	store.dispatch(
		{
			type: UPDATE_RV_PROPERTIES,
			payload: properties,
		} );
}

export function removeProperty( property: Property ) {
	getAppServices().storeService.getStore().dispatch(
		{
			type: RV_REMOVE_PROPERTY,
			payload: property,
		} );
}

export function updateProperty( property: Property ) {
	getAppServices().storeService.getStore().dispatch(
		{
			type: RV_UPDATE_PROPERTY,
			payload: property,
		} );
}

export function updateRVDocs( docs: RVDocument[] ) {
	getAppServices().storeService.getStore().dispatch(
		{
			type: RV_UPDATE_DOCS,
			payload: docs,
		} );
}

export function updateRVReason( reasonId: RVLookupX, comment: string ) {
	getAppServices().storeService.getStore().dispatch(
		{
			type: RV_UPDATE_REASON,
			payload: {
				id: reasonId,
				comment: comment,
			},
		} );
}

export function extractOData( property: Property ): Partial<RVProperty> {
	let p: Partial<RVProperty> = {
		AreaId: property.LandAreaId,
		AreaNameAr: property.AreaAr,
		AreaNameEn: property.AreaEn,
		BuildingEjPropertyId: Number( property.BuildingEjariPropertyId ),
		BuildingNameAr: property.BuildingNameAr,
		BuildingNameEn: property.BuildingNameEn,
		BuildingPropertyId: property.BuildingPropertyId,
		EjariSubTypeId: property.EjariSubTypeId,
		EjariSubTypeNameAr: property.EjariSubTypeNameAr,
		EjariSubTypeNameEn: property.EjariSubTypeNameEn,
		EjariTypeId: Number( property.EjariTypeId ) ? Number( property.EjariTypeId ) : undefined,
		EjariTypeNameAr: property.EjariTypeNameAr,
		EjariTypeNameEn: property.EjariTypeNameEn,
		EjariUsageId: Number( property.EjariPropertyUsageId ) ? Number( property.EjariPropertyUsageId ) : undefined,
		EjariUsageNameAr: property.EjariUsageNameAr,
		EjariUsageNameEn: property.EjariUsageNameEn,
		IsFreehold: property.IsFreeHold === true ? 1 : 0,
		IsRented: property.IsRented === true ? 1 : 0,
		LandDmNo: property.LandDmNo,
		LandDmSubNo: property.LandDmSubNo,
		LandEjPropertyId: Number( property.LandEjariPropertyId ),
		LandNo: property.LandNo,
		LandPropertyId: property.LandPropertyId,
		LandSubNo: property.LandSubNo,
		PropertyId: property.PropertyId,
		PropertyNumber: property.PropertyNumber,
		PropertySize: Number( property.Size ) ? Number( property.Size ) : undefined,
		PropertyTypeId: property.PropertyTypeId,
		UnitEjPropertyId: Number( property.UnitEjariPropertyId ),
		UnitPropertyId: property.UnitPropertyId,
		VillaEjPropertyId: Number( property.VillaEjariPropertyId ),
		VillaPropertyId: property.VillaPropertyId,
		DewaNumber: property.Dewa,
	};
	switch ( property.PropertyTypeId ) {
		case RVPropertyType.LAND:
			p.PropertyTypeNameEn = LVStrings.land.en;
			p.PropertyTypeNameAr = LVStrings.land.ar;
			break;
		case RVPropertyType.VILLA:
			p.PropertyTypeNameEn = LVStrings.villa.en;
			p.PropertyTypeNameAr = LVStrings.villa.ar;
			break;
		case RVPropertyType.UNIT:
			p.PropertyTypeNameEn = LVStrings.unit.en;
			p.PropertyTypeNameAr = LVStrings.unit.ar;
			break;
		case RVPropertyType.BUILDING:
			p.PropertyTypeNameEn = LVStrings.building.en;
			p.PropertyTypeNameAr = LVStrings.building.ar;
			break;
		default:
			break;
	}
	if ( property.IsRented && property.AssociatedContracts && property.AssociatedContracts.length > 0 ) {
		let c = property.AssociatedContracts[ 0 ];
		p.ContractNumber = c.ContractNumber;
		p.ContractStartDate = c.ContractStartDate;
		p.ContractEndDate = c.ContractEndDate;
		p.ContractAnnualAmount = c.ContractAmount;
	}
	if ( property.IsRented ) {
		p.IsCompletedProperty = 1;
	}
	// property wallet usage id > 2 is others user has to select
	if ( p.EjariUsageId && p.EjariUsageId > 2 ) {
		p.EjariUsageId = undefined;
		p.EjariUsageNameAr = undefined;
		p.EjariUsageNameEn = undefined;
		p.EjariTypeId = undefined;
		p.EjariTypeNameAr = undefined;
		p.EjariTypeNameEn = undefined;
		p.EjariSubTypeId = undefined;
		p.EjariSubTypeNameEn = undefined;
		p.EjariSubTypeNameAr = undefined;
	}
	return p;
}

function uploadPage( page: RVDocumentPage, documentId: number ): Promise<any> {
	return uploadDocument( page.file!, documentId + "" ).then( validateResponseX )
	.then( x => {
		page.remoteId = x.TemporaryRequestId;
		page.remoteUri = x.Url;
		return Promise.resolve( page );
	} );
}

function prepare(): Promise<Property[]> {
	const rv = getAppServices().storeService.getStore().getState().RValuation;
	const properties: Property[] = rv.properties!;
	if ( properties == undefined || properties.length === 0 )
		return Promise.reject( Strings.no_property );
	const uploadPromises: Promise<any>[] = [];
	for ( const property of properties ) {
		if ( property.rvData!.documents && property.rvData!.documents.length > 0 ) {
			const docs = property.rvData!.documents!;
			for ( const doc of docs ) {
				let pages = doc.pages;
				if ( pages && pages.length > 0 ) {
					for ( const page of pages ) {
						if ( page.remoteId == undefined ) {
							uploadPromises.push( uploadPage( page, doc.lookupId ) );
						}
					}

				}
			}
		}
	}
	if ( rv.documents && rv.documents.length > 0 ) {
		for ( const doc of rv.documents ) {
			let pages = doc.pages;
			if ( pages && pages.length > 0 ) {
				for ( const page of pages ) {
					if ( page.remoteId == undefined ) {
						uploadPromises.push( uploadPage( page, doc.lookupId ) );
					}
				}

			}
		}
	}
	return Promise.all( uploadPromises ).then( px => {
		let np: Property[] = [];
		for ( const p of properties ) {
			let property = _.cloneDeep( p );
			np.push( property );
			updateProperty( property );
		}
		return Promise.resolve( np );
	} );
}

function fillManagementCompanyInfo( request: RVRequest ): Promise<RVRequest> {
	if ( request.RequestorProfile.ManagementCompId != undefined && request.RequestorProfile.ManagementCompId !== 0 ) {
		return getMCInfo( request.RequestorProfile.ManagementCompId ).then( validateResponseX ).then( mc => {
			const p = request.RequestorProfile;
			if ( mc ) {
				p.LicenseNumber = mc.LicenseNumber;
				p.MangementCompNameEn = mc.NameEn;
				p.MangementCompNameAr = mc.NameAr;
			}
			return Promise.resolve( request );
		} );
	} else {
		return Promise.resolve( request );
	}
}

function makeRVRequest( properties: Property[] ): Promise<RVRequest> {
	const rv = getAppServices().storeService.getStore().getState().RValuation;
	const pages: RVDocumentPage[] = [];
	if ( properties && properties.length ) {
		for ( const property of properties ) {
			const docs = property.rvData!.documents!;
			if ( docs && docs.length ) {
				for ( const doc of docs ) {
					for ( const page of doc.pages ) {
						page.lookupId = doc.lookupId;
						pages.push( page );
					}
				}
			}
		}
	}
	if ( rv.documents && rv.documents.length > 0 ) {
		for ( const doc of rv.documents ) {
			for ( const page of doc.pages ) {
				page.lookupId = doc.lookupId;
				pages.push( page );
			}
		}
	}
	const attachments: Attachment[] = pages.map( page => {
		return {
			TemporaryRequestId: page.remoteId + "",
			DocumentId: page.lookupId,
		};
	} );
	const request: Partial<RVRequest> = {
		RequestReasonId: rv.reason?.Identity,
		RequestDescription: rv.comment,
		RequestChannelId: 1,
		Attachments: attachments,
		RequestorTypeId: rv.env.mode,
	};
	let name = getName();
	request.RequestorProfile = {
		...defProfile,
		Email: getEmail(),
		EmiratesId: getEmiratesID(),
		IsEjariUser: rv.env.isEjariUser ? 1 : 0,
		MobileNumber: getMobileNumber(),
	} as RequestorProfile;
	if ( rv.env.participantId )
		request.RequestorProfile.ParticipantId = rv.env.participantId;
	if ( rv.env.isManagementCompany ) {
		request.RequestorProfile.ManagementCompId = rv.env.managementId!;
	}
	request.RequestorProfile.UserAccountId = getEjariActiveUserId();
	request.Properties = properties.map( property => {
		return {
			...property.rvData!.oData,
			...property.rvData!.data,
		} as RVProperty;
	} );
	request.RequestorProfile.MobileNumber = rv.env.phoneNumber!;
	request.RequestorProfile.EmiratesId = rv.env.eidNumber!;
	request.RequestorProfile.UnifiedIdNumber = rv.env.uidNumber!;
	if ( name && name.en )
		request.RequestorProfile.PartNameEn = name.en;
	if ( name && name.ar )
		request.RequestorProfile.PartNameAr = name.ar;
	return Promise.resolve( request as RVRequest );
}

export function submitRVRequest() {
	return prepare()
	.then( makeRVRequest )
	.then( fillManagementCompanyInfo )
	.then()
	.then( api.RentalValuationApi.submitRVRequest )
	.then( validateResponse );
}

export function resetRVData() {
	getAppServices().storeService.getStore().dispatch(
		{
			type: RV_RESET,
		} );
}

export function populateRVStatus( req: AuditedRVRequest ) {
	let status = { en: req.RequestStatusNameEn, ar: req.RequestStatusNameAr };
	if ( req.RequestStatusId === 4 && req.AuditStatusId === 3 ) {
		status = { en: req.AuditStatusNameEn, ar: req.AuditStatusNameAr };
	}
	req.Status = status;
}

export function convertTabuPropertyIdRVPropertyType( pId: number ): RVPropertyType | undefined {
	switch ( pId ) {
		case 1:
			return RVPropertyType.LAND;
		case 2:
			return RVPropertyType.VILLA;
		case 3:
			return RVPropertyType.UNIT;
		case 4:
			return RVPropertyType.BUILDING;
		default:
			return undefined;
	}
}

export function getRVRequestStatusClass( statusId?: number, auditStatusId?: number ) {
	switch ( statusId ) {
		case RVRequestStatus.SUBMITTED:
			return "entered";
		case RVRequestStatus.PAYMENT_PENDING:
			return "pending-payment";
		case RVRequestStatus.EVALUATION_PENDING:
			return "pending";
		case RVRequestStatus.COMPLETED:
			if ( auditStatusId === 3 ) {
				return "pending";
			}
			return "completed";
		case RVRequestStatus.CANCELLED:
			return "cancelled";
		default :
			return "entered";
	}
}

